import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  forwardRef,
  inject,
  Injector,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { DebounceTime } from '@olymp/shared';
import { InputTextModule } from 'primeng/inputtext';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { SelectService } from '../../utils/select.service';
import { multiLangFilterBy } from '../../utils/multilang-filter.util';

const EMPTY_OPTIONS: unknown[] = [];

@Component({
  selector: 'lib-select-with-lazy',
  imports: [
    TranslocoModule,
    FormsModule,
    InputTextModule,
    SelectModule,
    InputGroupModule,
    InputGroupAddonModule,
    DebounceTime,
  ],
  template: `
    <div class="w-full">
      @if (label()) {
        <label [for]="label()"
          >{{ label() | transloco }}
          <span class="text-red-600">*</span>
        </label>
      }
      <p-select
        appendTo="body"
        [virtualScroll]="virtualScroll()"
        [virtualScrollItemSize]="38"
        [filter]="true"
        [options]="selectOptions()"
        [optionLabel]="optionLabel()"
        [optionValue]="optionValue()"
        [ngModel]="value()"
        (ngModelChange)="onViewModelChange($event)"
        [styleClass]="styleClass()"
        [virtualScrollOptions]="options"
        [filterBy]="resolveFilterBy()"
        [disabled]="isDisabled()"
        [placeholder]="placeholder()"
        [showClear]="showClear()"
      >
        @if (isMultiLang()) {
          <ng-template #selectedItem let-selectedOption>
            <span>{{ multilangLabel(selectedOption?.name) }}</span>
          </ng-template>
          <ng-template let-item #item>
            <span style="white-space: normal;">{{
              multilangLabel(item?.name)
            }}</span>
          </ng-template>
        }
        @if (isTranslated()) {
          <ng-template #selectedItem let-selectedOption>
            <span>{{ selectedOption[optionLabel()] | transloco }}</span>
          </ng-template>
          <ng-template let-item #item>
            <span style="white-space: normal;">{{
              item[optionLabel()] | transloco
            }}</span>
          </ng-template>
        }
        @if (virtualScroll()) {
          <ng-template #filter>
            <p-inputgroup>
              <p-inputgroup-addon>
                <i class="pi pi-search"></i>
              </p-inputgroup-addon>
              <input
                type="text"
                pInputText
                libDebounceTime
                [ngModel]="filterValue()"
                (ngModelChange)="filterValue.set($event)"
                (searchValue)="getValueWithFilter()"
              />
            </p-inputgroup>
          </ng-template>
        }
      </p-select>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DatePipe,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectWithLazy),
      multi: true,
    },
  ],
})
export class SelectWithLazy<T>
  extends SelectService<T>
  implements ControlValueAccessor, OnInit
{
  private readonly transloco = inject(TranslocoService);

  constructor() {
    super(inject(Injector));
    effect(() => {
      this.isDisabled.set(this.disabled());
    });
  }

  /** Ko‘p tilli optionlarda qidiruv barcha til maydonlari bo‘yicha ishlaydi. */
  resolveFilterBy(): string {
    const custom = this.filterFields()?.trim();
    if (custom) {
      return custom;
    }
    if (this.isMultiLang()) {
      return multiLangFilterBy(this.optionLabel());
    }
    return this.optionLabel();
  }

  /** Har CD’da yangi [] yaratmaslik — CD loop oldini oladi */
  selectOptions(): T[] {
    return (this.inputOptions() ?? this.data() ?? EMPTY_OPTIONS) as T[];
  }

  /**
   * languagePipe|async dropdown overlay’da CD loop beradi.
   * Sync label — qotishsiz.
   */
  multilangLabel(name: Record<string, string> | string | null | undefined): string {
    if (!name) {
      return '';
    }
    if (typeof name === 'string') {
      return name;
    }
    const lang = this.transloco.getActiveLang();
    return name[lang] || name['uz'] || name['ru'] || name['en'] || name['kaa'] || '';
  }

  /** p-select → form: bir xil qiymatda qayta yozib CD loop qilmasin */
  onViewModelChange(next: T): void {
    if (Object.is(this.value(), next)) {
      return;
    }
    this.change({ value: next } as SelectChangeEvent);
  }
}
